// @wander/shared — City disambiguation utilities
// Ported from web's WanderAppComplete.jsx addTripBuilderStop()

/**
 * Known ambiguous city names that exist in multiple countries.
 */
export const AMBIGUOUS_CITY_NAMES = [
  'santiago', 'cordoba', 'córdoba', 'granada', 'valencia', 'san jose', 'san josé',
  'portland', 'cambridge', 'richmond', 'springfield', 'columbus', 'georgetown',
  'victoria', 'hamilton', 'manchester', 'birmingham', 'perth', 'darwin',
  'napoli', 'naples', 'tripoli', 'alexandria', 'lima', 'cordova',
  'san fernando', 'santa cruz', 'monterrey', 'monterey', 'cartagena',
  'mérida', 'merida', 'leon', 'león', 'puebla', 'santiago de',
  'san antonio', 'san pedro', 'santa fe', 'santa rosa', 'las palmas',
  'florence', 'paris', 'dublin',
  // Caribbean / Central America ambiguities — surfaced by user feedback
  // (a trip to Antigua, Guatemala was generating venues in Antigua and
  // Barbuda because the LLM picked up the wrong "Antigua").
  'antigua', // Antigua, Guatemala vs. Antigua and Barbuda
  'st george', 'saint george', // Bermuda vs. Grenada vs. Utah
  'st john', 'saint john', // USVI vs. Antigua vs. Canada
  'kingston', // Jamaica vs. Ontario vs. UK
  'tripoli', // Libya vs. Lebanon
];

/**
 * Known country names for detecting bare country input.
 *
 * IMPORTANT: this list MUST stay in sync with COUNTRY_TO_MAJOR_CITY below
 * because the two are used together — `isBareCountry()` checks this list,
 * `resolveCountryToCity()` looks up the map. If a country is in the map but
 * not the list, `isBareCountry("Russia")` returns false, no primaryCityHint
 * gets injected, and the bare-country fix is silently bypassed.
 *
 * Built dynamically from COUNTRY_TO_MAJOR_CITY plus common aliases, so adding
 * a new country only needs an entry in the map below.
 */
const _COUNTRY_ALIASES = [
  // United States variants
  'usa', 'us', 'america', 'united states', 'united states of america',
  // United Kingdom variants
  'uk', 'britain', 'great britain', 'united kingdom',
  // Netherlands alias
  'holland',
];
export let KNOWN_COUNTRY_NAMES; // assigned after COUNTRY_TO_MAJOR_CITY below.

/**
 * Activity keywords that suggest a vague location needing resolution.
 */
export const ACTIVITY_KEYWORDS = [
  'skiing', 'ski', 'safari', 'diving', 'surfing', 'trekking',
  'hiking', 'wine tasting', 'wine tour'
];

/**
 * Check if a city name is ambiguous (exists in multiple countries).
 * Returns false if input already includes a comma (user specified country).
 */
export const isAmbiguousCity = (cityName) => {
  if (!cityName) return false;
  if (cityName.includes(',')) return false;
  const nameLower = cityName.toLowerCase().trim();
  return AMBIGUOUS_CITY_NAMES.some(name => nameLower === name || nameLower.startsWith(name + ' '));
};

/**
 * Maps bare country names to their primary tourist destination city.
 * Used server-side to resolve country-level inputs that would break
 * geography lookups, airport searches, and currency detection.
 */
export const COUNTRY_TO_MAJOR_CITY = {
  // Asia
  'japan': 'Tokyo, Japan', 'china': 'Beijing, China', 'india': 'Delhi, India',
  'thailand': 'Bangkok, Thailand', 'vietnam': 'Hanoi, Vietnam',
  'indonesia': 'Bali, Indonesia', 'malaysia': 'Kuala Lumpur, Malaysia',
  'philippines': 'Manila, Philippines', 'cambodia': 'Siem Reap, Cambodia',
  'laos': 'Luang Prabang, Laos', 'myanmar': 'Yangon, Myanmar',
  'nepal': 'Kathmandu, Nepal', 'sri lanka': 'Colombo, Sri Lanka',
  'mongolia': 'Ulaanbaatar, Mongolia', 'south korea': 'Seoul, South Korea',
  'korea': 'Seoul, South Korea', 'taiwan': 'Taipei, Taiwan',
  'singapore': 'Singapore', 'hong kong': 'Hong Kong',

  // Europe
  'france': 'Paris, France', 'spain': 'Barcelona, Spain', 'italy': 'Rome, Italy',
  'germany': 'Berlin, Germany', 'portugal': 'Lisbon, Portugal',
  'greece': 'Athens, Greece', 'turkey': 'Istanbul, Turkey',
  'croatia': 'Dubrovnik, Croatia', 'iceland': 'Reykjavik, Iceland',
  'norway': 'Oslo, Norway', 'sweden': 'Stockholm, Sweden',
  'finland': 'Helsinki, Finland', 'denmark': 'Copenhagen, Denmark',
  'switzerland': 'Zurich, Switzerland', 'austria': 'Vienna, Austria',
  'netherlands': 'Amsterdam, Netherlands', 'belgium': 'Brussels, Belgium',
  'ireland': 'Dublin, Ireland', 'scotland': 'Edinburgh, Scotland',
  'england': 'London, England', 'czech republic': 'Prague, Czech Republic',
  'czechia': 'Prague, Czech Republic', 'hungary': 'Budapest, Hungary',
  'poland': 'Krakow, Poland', 'romania': 'Bucharest, Romania',
  'slovenia': 'Ljubljana, Slovenia', 'estonia': 'Tallinn, Estonia',
  'latvia': 'Riga, Latvia', 'lithuania': 'Vilnius, Lithuania',
  'malta': 'Valletta, Malta', 'cyprus': 'Paphos, Cyprus',
  'slovakia': 'Bratislava, Slovakia', 'luxembourg': 'Luxembourg City, Luxembourg',
  'russia': 'Moscow, Russia',

  // Americas
  'canada': 'Toronto, Canada', 'mexico': 'Mexico City, Mexico',
  'brazil': 'Rio de Janeiro, Brazil', 'argentina': 'Buenos Aires, Argentina',
  'chile': 'Santiago, Chile', 'colombia': 'Bogota, Colombia',
  'peru': 'Lima, Peru', 'ecuador': 'Quito, Ecuador',
  'costa rica': 'San Jose, Costa Rica', 'panama': 'Panama City, Panama',
  'guatemala': 'Antigua, Guatemala', 'cuba': 'Havana, Cuba',
  'uruguay': 'Montevideo, Uruguay', 'bolivia': 'La Paz, Bolivia',

  // Middle East
  'united arab emirates': 'Dubai, UAE', 'uae': 'Dubai, UAE',
  'oman': 'Muscat, Oman', 'jordan': 'Amman, Jordan',
  'israel': 'Tel Aviv, Israel', 'lebanon': 'Beirut, Lebanon',
  'saudi arabia': 'Riyadh, Saudi Arabia', 'qatar': 'Doha, Qatar',

  // Africa
  'egypt': 'Cairo, Egypt', 'morocco': 'Marrakech, Morocco',
  'tunisia': 'Tunis, Tunisia', 'kenya': 'Nairobi, Kenya',
  'tanzania': 'Dar es Salaam, Tanzania', 'south africa': 'Cape Town, South Africa',
  'ethiopia': 'Addis Ababa, Ethiopia', 'ghana': 'Accra, Ghana',
  'senegal': 'Dakar, Senegal', 'madagascar': 'Antananarivo, Madagascar',
  'namibia': 'Windhoek, Namibia', 'botswana': 'Maun, Botswana',
  'rwanda': 'Kigali, Rwanda', 'uganda': 'Kampala, Uganda',
  'mozambique': 'Maputo, Mozambique', 'nigeria': 'Lagos, Nigeria',
  'zambia': 'Lusaka, Zambia', 'zimbabwe': 'Harare, Zimbabwe',
  'mauritius': 'Port Louis, Mauritius', 'seychelles': 'Victoria, Seychelles',

  // Oceania
  'australia': 'Sydney, Australia', 'new zealand': 'Auckland, New Zealand',
  'fiji': 'Nadi, Fiji', 'maldives': 'Male, Maldives',

  // United States / United Kingdom — covered via aliases in KNOWN_COUNTRY_NAMES
  // but also given canonical entries here so resolveCountryToCity works.
  'united states': 'New York, USA', 'usa': 'New York, USA', 'us': 'New York, USA',
  'america': 'New York, USA', 'united states of america': 'New York, USA',
  'united kingdom': 'London, UK', 'uk': 'London, UK',
  'britain': 'London, UK', 'great britain': 'London, UK',
  'holland': 'Amsterdam, Netherlands',
};

// Derive KNOWN_COUNTRY_NAMES dynamically from the COUNTRY_TO_MAJOR_CITY map +
// the alias list. Single source of truth — adding a country to the map above
// also adds it to the bare-country detector. Previously these were two
// hand-edited lists that drifted (the audit found Russia, Mozambique,
// Nigeria, Zambia, Zimbabwe, Slovenia, Estonia, Latvia, Lithuania, Malta,
// Cyprus, Slovakia, Luxembourg ALL in the map but missing from the list, so
// `isBareCountry("Russia")` returned false and the primaryCityHint never
// fired for those countries).
KNOWN_COUNTRY_NAMES = Array.from(new Set([
  ...Object.keys(COUNTRY_TO_MAJOR_CITY),
  ..._COUNTRY_ALIASES,
]));

/**
 * Resolve a bare country name to its major tourist city.
 * Returns null if the input is not a recognized bare country.
 * @param {string} name - destination name
 * @returns {string|null} - "City, Country" or null
 */
export const resolveCountryToCity = (name) => {
  if (!name) return null;
  const nameLower = name.toLowerCase().trim();
  return COUNTRY_TO_MAJOR_CITY[nameLower] || null;
};

/**
 * Check if input is a bare country name (not a city).
 */
export const isBareCountry = (name) => {
  if (!name || name.includes(',')) return false;
  const nameLower = name.toLowerCase().trim();
  return KNOWN_COUNTRY_NAMES.includes(nameLower);
};

/**
 * Check if input contains activity keywords suggesting a vague location.
 */
export const isActivityBasedLocation = (name) => {
  if (!name || name.includes(',')) return false;
  const nameLower = name.toLowerCase().trim();
  if (isBareCountry(name)) return false;
  return ACTIVITY_KEYWORDS.some(kw => nameLower.includes(kw));
};

/**
 * Build a disambiguation prompt for an LLM to resolve an ambiguous city name.
 * The actual LLM call is made by the caller (needs API client).
 *
 * @param {string} cityName — the ambiguous city name
 * @param {string[]} existingStops — names of other trip stops for context
 * @returns {{ system: string, userMessage: string }} — prompt parts for the LLM call
 */
export const buildDisambiguationPrompt = (cityName, existingStops = []) => {
  const stopsContext = existingStops.length > 0
    ? `Their other stops are: ${existingStops.join(', ')}. `
    : '';
  return {
    system: `You are a geography expert. A traveler is adding "${cityName}" as a destination. ${stopsContext}Which specific city do they most likely mean? Return ONLY "City, Country" format (e.g., "Córdoba, Spain" or "Santiago, Chile"). Nothing else.`,
    userMessage: `Which "${cityName}" does this traveler mean?`
  };
};

/**
 * Parse the LLM's disambiguation response.
 * Expects "City, Country" format.
 *
 * @param {string} response — raw LLM text
 * @param {string} originalName — the original ambiguous name
 * @returns {string|null} — resolved "City, Country" or null if invalid
 */
export const parseDisambiguationResponse = (response, originalName) => {
  if (!response) return null;
  const resolved = response.trim();
  if (resolved.includes(',') && resolved !== originalName) {
    return resolved;
  }
  return null;
};
